import { useState } from 'react'
import { useSelector } from 'react-redux'
import { ArrowPathRoundedSquareIcon } from '@heroicons/react/20/solid'
import { Dumbbell, Info } from 'lucide-react'
import { AppIsInTestEnvironment } from 'functions/environmentUtils'

import ExerciseImage from 'components/ExerciseImage'
import NumberInput from 'components/Input/NumberInput'
import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function OverrideInputModalRouteView({ 
    exercises, 
    modalData, 
    setsOverrideValue,
    setSetsOverrideValue,
    exerciseOverrideValue,
    repsOverrideValue,
    setRepsOverrideValue,
    showWeightRecommendationOverrideInput,
    weightRecommendationOverrideValue,
    setWeightRecommendationOverrideValue,
    InitializeWeightRecommendationComponent = () => {},
    weightRecommendationPreview,
    weightRecommendationPreviewInputs,
    setModalRoute, 
    OnCreateButton = () => {}
}) {

    const userType = useSelector(state => state.user.type)
    const organizationId = useSelector(state => state.user.organizationId)

    const [showExerciseSwapIcon, setShowExerciseSwapIcon] = useState(false)
    const [showWeightRecommendationTooltip, setShowWeightRecommendationTooltip] = useState(false)

    const NavigateToExerciseListModalRoute = () => {
        setModalRoute('exercise-list')
    }

    const HandleSetsOverrideChange = (value) => {
        if (value === 0) {
            setSetsOverrideValue(null)
            return
        }

        setSetsOverrideValue(value)
    }
    
    const HandleRepsOverrideChange = (value) => {
        if (value === 0) {
            setRepsOverrideValue(null)
            return
        }

        setRepsOverrideValue(value)
    }

    const HandleWeightRecommendationOverrideChange = (value) => {
        if (value === 0) {
            setWeightRecommendationOverrideValue(null)
            return
        }

        setWeightRecommendationOverrideValue(value)
    }

    const HandleWeightRecommendationBlur = () => {
        if (weightRecommendationOverrideValue === null) return

        const rounded = Math.round(weightRecommendationOverrideValue / 5) * 5
        const clamped = Math.min(1000, Math.max(5, rounded))

        setWeightRecommendationOverrideValue(clamped)
    }

    return (
        <div className="space-y-4">
            <div className='-mt-5 space-y-1'>
                <div className="text-xl text-left text-base font-semibold leading-6 text-gray-900">
                    Add Bulk Override
                </div>

                <p className='text-sm text-left'>
                    {modalData.draggedSlots.length} slot{modalData.draggedSlots.length > 1 ? 's' : ''} selected
                </p>
            </div>

            {/* one-off-sets-override */}

            {modalData.draggedSlots[0]?.dragType === 'phase' &&
                <div>
                    <p className='text-sm pl-1 pb-1'>Sets</p>

                    <div className='flex-1'>
                        <NumberInput
                            value={setsOverrideValue}
                            placeholder="Enter sets"
                            min={1}
                            onChange={(value) => HandleSetsOverrideChange(value)}
                            className={`
                                text-lg py-3
                                ${setsOverrideValue !== null ? 'bg-orange-100' : ''}
                            `}
                            autoFocus={true}
                            showArrows={false}
                        />
                    </div>
                </div>
            }

            {modalData.draggedSlots[0]?.dragType === 'slot' &&
                <>

                    {/* one-off-exercise-swap */}

                    <div>
                        <p className='text-sm pl-1 pb-1'>Exercise</p>

                        {exerciseOverrideValue === null &&
                            <div
                                className='flex-1 p-2 flex items-center gap-x-4 cursor-pointer border border-gray-400 rounded-lg'
                                onMouseEnter={() => setShowExerciseSwapIcon(true)}
                                onMouseLeave={() => setShowExerciseSwapIcon(false)}
                                onClick={NavigateToExerciseListModalRoute}
                            >
                                <div className='relative'>
                                    <div className='w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center'>
                                        <Dumbbell className='w-8 h-8 text-gray-500' />
                                    </div>

                                    {showExerciseSwapIcon &&
                                        <div className='absolute top-0 right-0 p-2 bg-gray-600/50 rounded-md'>
                                            <ArrowPathRoundedSquareIcon className='w-8 h-8 text-white' />
                                        </div>
                                    }
                                </div>

                                <div className='text-left'>Select Exercise</div>
                            </div>
                        }

                        {exerciseOverrideValue !== null &&
                            <div
                                className='flex-1 p-2 flex items-center gap-x-4 cursor-pointer border border-gray-400 rounded-lg bg-orange-100'
                                onMouseEnter={() => setShowExerciseSwapIcon(true)}
                                onMouseLeave={() => setShowExerciseSwapIcon(false)}
                                onClick={NavigateToExerciseListModalRoute}
                            >
                                <div className='relative'>
                                    <ExerciseImage
                                        exercise={exerciseOverrideValue}
                                        exercises={exercises}
                                    />

                                    {showExerciseSwapIcon &&
                                        <div className='absolute top-0 right-0 p-2 bg-gray-600/50 rounded-md'>
                                            <ArrowPathRoundedSquareIcon className='w-8 h-8 text-white' />
                                        </div>
                                    }
                                </div>

                                <div className='text-left'>{exerciseOverrideValue.name}</div>
                            </div>
                        }
                    </div>

                    {/* one-off-reps-override */}

                    <div>
                        <p className='text-sm pl-1 pb-1'>Reps</p>

                        <div className='flex-1'>
                            <NumberInput
                                value={repsOverrideValue}
                                placeholder="Enter reps"
                                min={1}
                                onChange={(value) => HandleRepsOverrideChange(value)}
                                onBlur={InitializeWeightRecommendationComponent}
                                className={`
                                    text-lg py-3
                                    ${repsOverrideValue !== null ? 'bg-orange-100' : ''}
                                `}
                                showArrows={false}
                            />
                        </div>
                    </div>

                    {/* one-off-weight-recommendation-override */}

                    {showWeightRecommendationOverrideInput && (
                        <div className='space-y-1'>
                            <p className='text-sm pl-1'>Weight Recommendation (nearest 5 lbs)</p>

                            <div className='flex-1'>
                                <NumberInput
                                    value={weightRecommendationOverrideValue}
                                    placeholder="Enter weight recommendation"
                                    min={5}
                                    max={1000}
                                    onChange={(value) => HandleWeightRecommendationOverrideChange(value)}
                                    onBlur={HandleWeightRecommendationBlur}
                                    className={`
                                        text-lg py-3
                                        ${weightRecommendationOverrideValue !== null ? 'bg-orange-100' : ''}
                                    `}
                                    showArrows={false}
                                />
                            </div>

                            {weightRecommendationPreview === null &&
                                <div className='h-5 w-1/2 bg-gray-200 rounded-md animate-pulse' />
                            }

                            {weightRecommendationPreview !== null &&
                                <div className='flex items-center gap-x-2'>
                                    <div className='pl-1 text-sm text-gray-500 flex-grow'>
                                        Auto weight recommendation: {weightRecommendationPreview.weightRecommendationRounded} lbs
                                    </div>

                                    {weightRecommendationPreviewInputs && (
                                        <div 
                                            className='relative'
                                            onMouseEnter={() => setShowWeightRecommendationTooltip(true)}
                                            onMouseLeave={() => setShowWeightRecommendationTooltip(false)}
                                        >
                                            <Info className='w-4 h-4 text-gray-400 cursor-help' />
                                            
                                            {showWeightRecommendationTooltip && (
                                                <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-gray-800 text-white  rounded-md shadow-xl z-30">
                                                    <div className="space-y-1">
                                                        <div className="text-xs">
                                                            <span className="text-xs font-semibold">Exercise:</span> {weightRecommendationPreviewInputs.exercise?.name || 'N/A'}
                                                        </div>

                                                        <div className="text-xs">
                                                            <span className="text-xs font-semibold">Reps:</span> {weightRecommendationPreviewInputs.reps || 'N/A'}
                                                        </div>

                                                        {/* Only create exercise button for Kinesis internal org and super admins */}
                                                        {(AppIsInTestEnvironment() || organizationId === 1 || userType === 'super-admin') && (
                                                            <div className="text-xs">
                                                                <span className="text-xs font-semibold">Formula: </span> {weightRecommendationPreview.formulaTag}
                                                            </div>
                                                        )}

                                                        {weightRecommendationPreviewInputs.taperData?.useTaper && (
                                                            <div className="text-xs">
                                                                <span className="text-xs font-semibold">Tapered Load: </span>
                                                                {(weightRecommendationPreviewInputs.taperData?.taperDampener * 100) || 100}% of max
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            }
                        </div>
                    )}
                </>
            }

            <BrandColorButton
                text="Create Overrides"
                action={OnCreateButton}
            />
        </div>
    )
}